import React, { useState, useEffect } from 'react';
import { 
  Row, Col, Card, Button, Accordion, InputGroup, Form, Badge, Modal, Alert, Spinner 
} from 'react-bootstrap';
import { 
  FaQuestionCircle, FaPlus, FaEdit, FaTrash, FaSearch, FaFilter
} from 'react-icons/fa';
import { useAuth } from '../hooks/useAuth';
import { useApi } from '../context/ApiContext';
import { toast } from 'react-toastify';

const FAQ = () => {
  const { user } = useAuth();
  const { apiCall } = useApi();
  
  // States
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Filter states
  const [filters, setFilters] = useState({
    search: ''
  });
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentFaq, setCurrentFaq] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' or 'edit'
  
  // Form state
  const [formData, setFormData] = useState({
    question_tr: '',
    question_en: '',
    answer_tr: '',
    answer_en: '',
    category: 'general',
    order_index: 0
  });
  
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      await loadFaqs();
      setError(null);
    } catch (err) {
      console.error('Error loading FAQ data:', err);
      setError(err.message || 'Veri yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const loadFaqs = async () => {
    const endpoint = isAdmin ? '/api/faq/admin/all' : '/api/faq/public';
    const params = new URLSearchParams();
    
    if (filters.search) params.append('search', filters.search);
    params.append('language', 'tr'); // Always Turkish
    
    const queryString = params.toString();
    const url = queryString ? `${endpoint}?${queryString}` : endpoint;
    
    const response = await apiCall('GET', url);
    setFaqs(Array.isArray(response) ? response : response.data || []);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const handleCreate = () => {
    setCurrentFaq(null);
    setModalMode('create');
    setFormData({
      question_tr: '',
      question_en: '',
      answer_tr: '',
      answer_en: '',
      category: 'general',
      order_index: 0
    });
    setShowModal(true);
  };

  const handleEdit = (faq) => {
    setCurrentFaq(faq);
    setModalMode('edit');
    setFormData({
      question_tr: faq.question_tr || '',
      question_en: faq.question_en || '',
      answer_tr: faq.answer_tr || '',
      answer_en: faq.answer_en || '',
      category: faq.category || 'general',
      order_index: faq.order_index || 0
    });
    setShowModal(true);
  };

  const handleDelete = (faq) => {
    setCurrentFaq(faq);
    setShowDeleteModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const endpoint = modalMode === 'create' ? '/api/faq' : `/api/faq/${currentFaq.id}`;
      const method = modalMode === 'create' ? 'POST' : 'PUT';
      
      await apiCall(method, endpoint, formData);
      
      toast.success(`FAQ ${modalMode === 'create' ? 'oluşturuldu' : 'güncellendi'}!`);
      setShowModal(false);
      loadFaqs();
    } catch (error) {
      toast.error(`Hata: ${error.message}`);
    }
  };

  const confirmDelete = async () => {
    try {
      await apiCall('DELETE', `/api/faq/${currentFaq.id}`);
      toast.success('FAQ silindi!');
      setShowDeleteModal(false);
      loadFaqs();
    } catch (error) {
      toast.error(`Hata: ${error.message}`);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // Helper functions for category display
  const getCategoryIcon = (category) => {
    const icons = {
      'general': '📋',
      'visa_application': '✈️',
      'appointments': '📅',
      'account': '👤',
      'support': '💡'
    };
    return icons[category] || '📌';
  };

  const getCategoryName = (category) => {
    const names = {
      'general': 'Genel',
      'visa_application': 'Vize Başvurusu',
      'appointments': 'Randevular',
      'account': 'Hesap Yönetimi',
      'support': 'Teknik Destek'
    };
    return names[category] || category.charAt(0).toUpperCase() + category.slice(1).replace('_', ' ');
  };

  // Group FAQs by category
  const groupedFaqs = faqs.reduce((groups, faq) => {
    const category = faq.category || 'general';
    if (!groups[category]) {
      groups[category] = [];
    }
    groups[category].push(faq);
    return groups;
  }, {});

  // Define category order
  const categoryOrder = ['general', 'visa_application', 'appointments', 'account', 'support'];
  
  // Get sorted category keys
  const sortedCategories = categoryOrder.filter(cat => groupedFaqs[cat]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Yükleniyor...</span>
        </Spinner>
      </div>
    );
  }

  return (
    <div>
      {/* Page Header */}
      <div className="mb-4">
        <Row className="align-items-center">
          <Col>
            <h1 className="h3 mb-2">
              <FaQuestionCircle className="me-2 text-primary" />
              Sıkça Sorulan Sorular
            </h1>
            <p className="text-muted mb-0">
              {isAdmin ? 'FAQ\'ları yönetin ve düzenleyin' : 'Sık sorulan soruları görüntüleyin'}
            </p>
          </Col>
          {isAdmin && (
            <Col xs="auto">
              <Button 
                variant="primary" 
                onClick={handleCreate}
                className="d-flex align-items-center"
              >
                <FaPlus className="me-2" />
                Yeni FAQ
              </Button>
            </Col>
          )}
        </Row>
      </div>

      {/* Filters */}
      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="g-3 align-items-center faq-filters">
            {/* Search */}
            <Col lg={10}>
              <InputGroup size="lg">
                <InputGroup.Text className="bg-white border-end-0">
                  <FaSearch className="text-muted" />
                </InputGroup.Text>
                <Form.Control
                  type="text"
                  placeholder="SSS'de ara..."
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="border-start-0 ps-0"
                  style={{ fontSize: '1rem' }}
                />
              </InputGroup>
            </Col>

            <Col lg={2} md={12}>
              <div className="text-center text-lg-end">
                <Badge 
                  bg="light" 
                  text="dark" 
                  className="px-3 py-2"
                  style={{ fontSize: '0.9rem' }}
                >
                  <FaFilter className="me-2" />
                  {faqs.length} Sonuç
                </Badge>
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* Error Display */}
      {error && (
        <Alert variant="danger" className="mb-4">
          <strong>Hata:</strong> {error}
        </Alert>
      )}

      {/* FAQ List */}
      {faqs.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaQuestionCircle size={64} className="text-muted mb-3" style={{ opacity: 0.5 }} />
            <h5 className="mb-2">FAQ Bulunamadı</h5>
            <p className="text-muted mb-0">
              {filters.search 
                ? 'Arama kriterlerinize uygun FAQ bulunamadı.' 
                : 'Henüz FAQ eklenmemiş.'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <Accordion className="mb-3">
          {sortedCategories.map((category, categoryIndex) => (
            <Accordion.Item 
              key={category} 
              eventKey={categoryIndex.toString()}
              className="mb-3 border-0 shadow-sm"
              style={{ borderRadius: '12px', overflow: 'hidden' }}
            >
              <Accordion.Header className="faq-category-accordion-header">
                <div className="d-flex align-items-center w-100">
                  <div 
                    className="faq-category-icon-box me-3 d-flex align-items-center justify-content-center"
                    style={{
                      width: '48px',
                      height: '48px',
                      borderRadius: '12px',
                      backgroundColor: 'var(--primary-color)',
                      color: 'white',
                      fontSize: '1.5rem',
                      flexShrink: 0
                    }}
                  >
                    {getCategoryIcon(category)}
                  </div>
                  <div>
                    <h5 className="mb-0 fw-bold" style={{ color: 'var(--gray-800)' }}>
                      {getCategoryName(category)}
                    </h5>
                    <p className="mb-0 text-muted small">
                      {groupedFaqs[category].length} soru
                    </p>
                  </div>
                </div>
              </Accordion.Header>
              
              <Accordion.Body className="p-0">
                {/* Category FAQs */}
                <Accordion className="faq-accordion">
                  {groupedFaqs[category].map((faq, index) => (
                    <Accordion.Item 
                      key={faq.id} 
                      eventKey={`${category}-${index}`}
                    >
                        <Accordion.Header>
                          <div className="d-flex justify-content-between align-items-center w-100 me-3">
                            <div className="flex-grow-1">
                              <strong>{faq.question || faq.question_tr}</strong>
                              {isAdmin && !faq.is_active && (
                                <Badge 
                                  bg="warning" 
                                  text="dark"
                                  className="ms-2"
                                  style={{ fontSize: '0.7rem', fontWeight: '500' }}
                                >
                                  Pasif
                                </Badge>
                              )}
                            </div>
                            {isAdmin && (
                              <div className="d-flex gap-2 ms-3">
                                <Button
                                  variant="outline-primary"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleEdit(faq);
                                  }}
                                >
                                  <FaEdit />
                                </Button>
                                <Button
                                  variant="outline-danger"
                                  size="sm"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleDelete(faq);
                                  }}
                                >
                                  <FaTrash />
                                </Button>
                              </div>
                            )}
                          </div>
                        </Accordion.Header>
                        <Accordion.Body className="bg-light">
                          <div className="faq-answer" style={{ 
                            fontSize: '0.95rem', 
                            lineHeight: '1.7',
                            color: '#444'
                          }}>
                            {faq.answer || faq.answer_tr}
                          </div>
                          {isAdmin && (
                            <div className="mt-4 pt-3 border-top" style={{ fontSize: '0.85rem' }}>
                              <Row className="text-muted">
                                <Col sm={6}>
                                  <strong>Sıra:</strong> <Badge bg="secondary" className="ms-1">{faq.order_index}</Badge>
                                </Col>
                                <Col sm={6}>
                                  <strong>Oluşturulma:</strong> {' '}
                                  <span className="text-dark">
                                    {new Date(faq.created_at).toLocaleDateString('tr-TR', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric'
                                    })}
                                  </span>
                                </Col>
                              </Row>
                            </div>
                          )}
                        </Accordion.Body>
                      </Accordion.Item>
                    ))}
                  </Accordion>
              </Accordion.Body>
            </Accordion.Item>
          ))}
        </Accordion>
      )}

      {/* FAQ Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {modalMode === 'create' ? 'Yeni FAQ Oluştur' : 'FAQ Düzenle'}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Row className="g-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Kategori *</Form.Label>
                  <Form.Control
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Sıra</Form.Label>
                  <Form.Control
                    type="number"
                    name="order_index"
                    value={formData.order_index}
                    onChange={handleInputChange}
                    min="0"
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Soru (Türkçe) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="question_tr"
                    value={formData.question_tr}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Question (English)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="question_en"
                    value={formData.question_en}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="g-3 mt-2">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Cevap (Türkçe) *</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="answer_tr"
                    value={formData.answer_tr}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>Answer (English)</Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={5}
                    name="answer_en"
                    value={formData.answer_en}
                    onChange={handleInputChange}
                  />
                </Form.Group>
              </Col>
            </Row>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowModal(false)}>
              İptal
            </Button>
            <Button variant="primary" type="submit">
              {modalMode === 'create' ? 'Oluştur' : 'Güncelle'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteModal} onHide={() => setShowDeleteModal(false)}>
        <Modal.Header closeButton>
          <Modal.Title>FAQ Silme Onayı</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Bu FAQ'yı silmek istediğinizden emin misiniz?</p>
          {currentFaq && (
            <div className="bg-light p-3 rounded">
              <strong>Soru:</strong> {currentFaq.question_tr}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)}>
            İptal
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            Sil
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default FAQ; 